import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { IdentityProfile } from "../types";

interface UseIdentityResult {
  readonly profiles: readonly IdentityProfile[];
  readonly loading: boolean;
  readonly createProfile: (data: {
    name: string;
    iconColor?: string;
    content?: string;
    is_default?: boolean;
    group_ids?: string[];
  }) => Promise<IdentityProfile>;
  readonly updateProfile: (
    id: string,
    updates: {
      name?: string;
      iconColor?: string;
      content?: string;
      is_default?: boolean;
      group_ids?: string[];
    },
  ) => Promise<void>;
  readonly deleteProfile: (id: string) => Promise<void>;
  readonly reload: () => Promise<void>;
}

export function useIdentity(): UseIdentityResult {
  const runtime = useAppRuntime();
  const [profiles, setProfiles] = useState<IdentityProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const result = await runtime.transport.rpc<IdentityProfile[]>("identity.list");
      setProfiles(result);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const createProfile = useCallback(
    async (data: {
      name: string;
      iconColor?: string;
      content?: string;
      is_default?: boolean;
      group_ids?: string[];
    }): Promise<IdentityProfile> => {
      const profile = await runtime.transport.rpc<IdentityProfile>("identity.create", data);
      if (profile.is_default) {
        setProfiles((prev) =>
          prev.map((p) => ({ ...p, is_default: false })),
        );
      }
      setProfiles((prev) => [profile, ...prev]);
      return profile;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const updateProfile = useCallback(
    async (
      id: string,
      updates: {
        name?: string;
        iconColor?: string;
        content?: string;
        is_default?: boolean;
        group_ids?: string[];
      },
    ): Promise<void> => {
      const updated = await runtime.transport.rpc<IdentityProfile>("identity.update", { id, ...updates });
      setProfiles((prev) => {
        let list = prev.map((p) => (p.id === id ? updated : p));
        if (updated.is_default) {
          list = list.map((p) => (p.id !== id ? { ...p, is_default: false } : p));
        }
        return list;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const deleteProfile = useCallback(async (id: string): Promise<void> => {
    await runtime.transport.rpc("identity.delete", { id });
    setProfiles((prev) => prev.filter((p) => p.id !== id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { profiles, loading, createProfile, updateProfile, deleteProfile, reload: load };
}
